/**
 * The pooled particle system.
 *
 * Renderer choice: **`ParticleContainer`**, not a hand-rolled `Mesh`/`Shader`.
 * In Pixi v8 `ParticleContainer` is already the instanced path — it packs every
 * particle into one interleaved vertex buffer with a generated upload function
 * and issues a single `drawElements` per container, with no per-particle
 * `Container` bookkeeping, no transform tree, and no bounds work (its bounds are
 * a hard-coded empty rect). A custom `Mesh` would have to re-implement exactly
 * that and would only start to win if we needed a bespoke fragment shader per
 * particle, which we do not: shape comes from a procedurally baked atlas and
 * colour comes from the per-vertex tint. So we get one draw call for every
 * additive spark on screen and one more for the non-additive dust, which is as
 * cheap as this gets.
 *
 * There are exactly two containers because there are exactly two blend modes:
 *
 * - `add`    — everything that glows: sparks, shards, rings, glints, motes.
 * - `normal` — everything that is matter rather than light: bust dust and the
 *              tumbling gold confetti, which must read as solid objects.
 *
 * Both share one texture source, so neither breaks the other's batch.
 *
 * Allocation policy: every `Spark` is created once in the constructor and never
 * again. Spawning pops an index off a free stack; when the stack is empty the
 * oldest live spark is stolen rather than a new one allocated, so the hot path
 * is provably garbage-free and the pool size is a hard ceiling. Dead sparks are
 * swap-removed from their active array in `update()`, which is also the array
 * the container renders from — no copying, no per-frame arrays.
 *
 * Everything visual is derived: colour from `@/core/tokens`, geometry and speed
 * from `CARD`/`DESIGN` in `@/pixi/layout`, so the effects scale with the table
 * instead of being pinned to a magic pixel size.
 */

import {
  CanvasSource,
  Container,
  ParticleContainer,
  Rectangle,
  Texture,
  type IParticle,
} from 'pixi.js';

import { palette } from '@/core/tokens';
import { LADDER_MAX, ladderColour, momentum } from '@/vfx/streakLadder';
import { CARD, DESIGN } from '@/pixi/layout';

/** Low enough to be invisible, high enough not to be culled. */
const WARM_ALPHA = 0.002;

/* ------------------------------------------------------------------ maths -- */

const TAU = Math.PI * 2;

function rand(min: number, max: number): number {
  return min + Math.random() * (max - min);
}

function randSign(): number {
  return Math.random() < 0.5 ? -1 : 1;
}

function clamp(value: number, min: number, max: number): number {
  return value < min ? min : value > max ? max : value;
}

/** Linear blend of two `0xRRGGBB` colours. Used to spread a burst across a hue. */
function mixRgb(a: number, b: number, t: number): number {
  const ar = (a >> 16) & 0xff;
  const ag = (a >> 8) & 0xff;
  const ab = a & 0xff;
  const br = (b >> 16) & 0xff;
  const bg = (b >> 8) & 0xff;
  const bb = b & 0xff;
  return (
    (((ar + (br - ar) * t) | 0) << 16) |
    (((ag + (bg - ag) * t) | 0) << 8) |
    ((ab + (bb - ab) * t) | 0)
  );
}

/**
 * Pixi packs a particle's tint as **BGR** with alpha in the top byte, so the
 * palette's RGB has to be byte-swapped once at spawn time. Mirrors
 * `Particle._updateColor()`.
 */
function toBgr(rgb: number): number {
  return ((rgb & 0xff) << 16) | (rgb & 0xff00) | ((rgb >>> 16) & 0xff);
}

/** Table-relative size. Keeps every dimension tied to the card, not to pixels. */
function cw(fraction: number): number {
  return CARD.width * fraction;
}

/** One card width per second — the base speed unit for every burst. */
const SPEED = CARD.width;

/** Longest simulation step we will take, so a backgrounded tab cannot teleport particles. */
const MAX_STEP = 1 / 20;

/* ------------------------------------------------------------------ atlas -- */

/**
 * The shape vocabulary. Every cell is baked white-on-transparent and tinted
 * per particle, so one texture source covers the whole system.
 */
const SHAPES = ['dot', 'spark', 'ring', 'shard', 'chip', 'dust', 'glow', 'mote'] as const;

export type SparkShape = (typeof SHAPES)[number];

const CELL = 128;
/** Gutter between cells so bilinear filtering cannot pull a neighbour in. */
const GUTTER = 2;
const FRAME = CELL - GUTTER * 2;
const INV_FRAME = 1 / FRAME;
const ATLAS_COLS = 4;
const ATLAS_ROWS = 2;

/**
 * The atlas is a pure alpha mask — the only literal colour in this file, and it
 * is not a palette choice: white is the identity for the per-particle tint that
 * every visible colour actually comes from.
 */
function white(alpha: number): string {
  return `rgba(255,255,255,${alpha.toFixed(4)})`;
}

function drawDot(ctx: CanvasRenderingContext2D, s: number): void {
  const r = s * 0.5;
  const g = ctx.createRadialGradient(0, 0, 0, 0, 0, r);
  g.addColorStop(0, white(1));
  g.addColorStop(0.18, white(0.92));
  g.addColorStop(0.42, white(0.4));
  g.addColorStop(0.72, white(0.1));
  g.addColorStop(1, white(0));
  ctx.fillStyle = g;
  ctx.beginPath();
  ctx.arc(0, 0, r, 0, TAU);
  ctx.fill();
}

function drawGlow(ctx: CanvasRenderingContext2D, s: number): void {
  const r = s * 0.5;
  const g = ctx.createRadialGradient(0, 0, 0, 0, 0, r);
  g.addColorStop(0, white(0.62));
  g.addColorStop(0.25, white(0.32));
  g.addColorStop(0.5, white(0.12));
  g.addColorStop(0.78, white(0.03));
  g.addColorStop(1, white(0));
  ctx.fillStyle = g;
  ctx.beginPath();
  ctx.arc(0, 0, r, 0, TAU);
  ctx.fill();
}

function drawMote(ctx: CanvasRenderingContext2D, s: number): void {
  const r = s * 0.5;
  const g = ctx.createRadialGradient(0, 0, 0, 0, 0, r);
  g.addColorStop(0, white(0.5));
  g.addColorStop(0.3, white(0.16));
  g.addColorStop(1, white(0));
  ctx.fillStyle = g;
  ctx.beginPath();
  ctx.arc(0, 0, r, 0, TAU);
  ctx.fill();
  ctx.fillStyle = white(1);
  ctx.beginPath();
  ctx.arc(0, 0, s * 0.085, 0, TAU);
  ctx.fill();
}

function drawSpark(ctx: CanvasRenderingContext2D, s: number): void {
  const core = ctx.createRadialGradient(0, 0, 0, 0, 0, s * 0.16);
  core.addColorStop(0, white(1));
  core.addColorStop(0.5, white(0.5));
  core.addColorStop(1, white(0));
  ctx.fillStyle = core;
  ctx.beginPath();
  ctx.arc(0, 0, s * 0.16, 0, TAU);
  ctx.fill();

  const arm = (length: number, halfWidth: number, angle: number): void => {
    ctx.save();
    ctx.rotate(angle);
    const g = ctx.createLinearGradient(0, 0, length, 0);
    g.addColorStop(0, white(0.95));
    g.addColorStop(0.22, white(0.46));
    g.addColorStop(1, white(0));
    ctx.fillStyle = g;
    ctx.beginPath();
    ctx.moveTo(0, -halfWidth);
    ctx.lineTo(length, 0);
    ctx.lineTo(0, halfWidth);
    ctx.closePath();
    ctx.fill();
    ctx.restore();
  };

  for (let i = 0; i < 4; i++) arm(s * 0.5, s * 0.042, (i * Math.PI) / 2);
  for (let i = 0; i < 4; i++) arm(s * 0.26, s * 0.026, Math.PI / 4 + (i * Math.PI) / 2);
}

function drawRing(ctx: CanvasRenderingContext2D, s: number): void {
  const r = s * 0.5;
  const g = ctx.createRadialGradient(0, 0, 0, 0, 0, r);
  g.addColorStop(0, white(0));
  g.addColorStop(0.5, white(0));
  g.addColorStop(0.63, white(0.3));
  g.addColorStop(0.76, white(1));
  g.addColorStop(0.87, white(0.26));
  g.addColorStop(1, white(0));
  ctx.fillStyle = g;
  ctx.beginPath();
  ctx.arc(0, 0, r, 0, TAU);
  ctx.fill();
}

function drawShard(ctx: CanvasRenderingContext2D, s: number): void {
  const half = s * 0.5;
  const h = s * 0.055;
  ctx.shadowColor = white(0.85);
  ctx.shadowBlur = s * 0.05;
  const g = ctx.createLinearGradient(-half, 0, half, 0);
  g.addColorStop(0, white(0));
  g.addColorStop(0.18, white(0.34));
  g.addColorStop(0.5, white(1));
  g.addColorStop(0.82, white(0.34));
  g.addColorStop(1, white(0));
  ctx.fillStyle = g;
  ctx.beginPath();
  ctx.moveTo(-half, 0);
  ctx.lineTo(-half * 0.3, -h);
  ctx.lineTo(half * 0.3, -h);
  ctx.lineTo(half, 0);
  ctx.lineTo(half * 0.3, h);
  ctx.lineTo(-half * 0.3, h);
  ctx.closePath();
  ctx.fill();
  ctx.shadowBlur = 0;
}

function drawChip(ctx: CanvasRenderingContext2D, s: number): void {
  const w = s * 0.56;
  const h = s * 0.38;
  const r = s * 0.06;
  ctx.shadowColor = white(0.8);
  ctx.shadowBlur = s * 0.03;
  ctx.fillStyle = white(0.95);
  ctx.beginPath();
  ctx.roundRect(-w / 2, -h / 2, w, h, r);
  ctx.fill();
  ctx.shadowBlur = 0;

  // A brighter top edge so a tumbling chip picks up the table's raking key light
  // instead of reading as a flat rectangle.
  const g = ctx.createLinearGradient(0, -h / 2, 0, h / 2);
  g.addColorStop(0, white(0.55));
  g.addColorStop(0.45, white(0));
  g.addColorStop(1, white(0));
  ctx.fillStyle = g;
  ctx.beginPath();
  ctx.roundRect(-w / 2, -h / 2, w, h, r);
  ctx.fill();
}

function drawDust(ctx: CanvasRenderingContext2D, s: number): void {
  const blobs: readonly (readonly [number, number, number, number])[] = [
    [0, 0, 0.24, 0.42],
    [-0.16, -0.1, 0.17, 0.34],
    [0.18, 0.08, 0.15, 0.3],
    [0.05, -0.2, 0.12, 0.26],
    [-0.1, 0.2, 0.13, 0.28],
  ];
  for (const [dx, dy, r, a] of blobs) {
    const cx = dx * s;
    const cy = dy * s;
    const radius = r * s;
    const g = ctx.createRadialGradient(cx, cy, 0, cx, cy, radius);
    g.addColorStop(0, white(a));
    g.addColorStop(0.5, white(a * 0.45));
    g.addColorStop(1, white(0));
    ctx.fillStyle = g;
    ctx.beginPath();
    ctx.arc(cx, cy, radius, 0, TAU);
    ctx.fill();
  }
}

const PAINTERS: Record<SparkShape, (ctx: CanvasRenderingContext2D, s: number) => void> = {
  dot: drawDot,
  spark: drawSpark,
  ring: drawRing,
  shard: drawShard,
  chip: drawChip,
  dust: drawDust,
  glow: drawGlow,
  mote: drawMote,
};

/**
 * Bakes the shape atlas once. Pure canvas work — no Assets, nothing to load, so
 * the system is usable on the first frame.
 */
function bakeAtlas(): { source: CanvasSource; frames: Record<SparkShape, Texture> } {
  const canvas = document.createElement('canvas');
  canvas.width = CELL * ATLAS_COLS;
  canvas.height = CELL * ATLAS_ROWS;

  const source = new CanvasSource({
    resource: canvas,
    resolution: 1,
    scaleMode: 'linear',
    addressMode: 'clamp-to-edge',
    label: 'vfx-particle-atlas',
  });

  const ctx = canvas.getContext('2d');
  if (ctx) {
    // `lighter` so overlapping glows in a single cell sum rather than replace.
    ctx.globalCompositeOperation = 'lighter';
    SHAPES.forEach((shape, i) => {
      const col = i % ATLAS_COLS;
      const row = (i / ATLAS_COLS) | 0;
      ctx.save();
      ctx.translate(col * CELL + CELL / 2, row * CELL + CELL / 2);
      PAINTERS[shape](ctx, FRAME);
      ctx.restore();
    });
  }
  source.update();

  const frames = {} as Record<SparkShape, Texture>;
  SHAPES.forEach((shape, i) => {
    const col = i % ATLAS_COLS;
    const row = (i / ATLAS_COLS) | 0;
    frames[shape] = new Texture({
      source,
      frame: new Rectangle(col * CELL + GUTTER, row * CELL + GUTTER, FRAME, FRAME),
      label: `vfx-${shape}`,
    });
  });

  return { source, frames };
}

/* ------------------------------------------------------------------ spark -- */

/**
 * One particle. Implements `IParticle` so the container can upload it directly;
 * everything below `texture` is simulation state the renderer never sees.
 *
 * Fields are plain and mutable on purpose — this object is written to sixty
 * times a second and reused forever.
 */
class Spark implements IParticle {
  /* IParticle */
  x = 0;
  y = 0;
  scaleX = 0;
  scaleY = 0;
  anchorX = 0.5;
  anchorY = 0.5;
  rotation = 0;
  color = 0;
  texture: Texture;

  /* pooling */
  readonly index: number;
  additive = true;

  /* simulation */
  vx = 0;
  vy = 0;
  /** Downward acceleration. Negative floats the spark upward. */
  gravity = 0;
  /** Velocity damping; higher values make a burst snap out and stop. */
  drag = 0;
  /** Radians per second the velocity vector is rotated by, for swirling bursts. */
  swirl = 0;
  spin = 0;
  /** Seconds before the spark starts living. Drives staggered, cascading bursts. */
  delay = 0;
  life = 0;
  maxLife = 1;
  /** Size in design units at birth and at death; linearly interpolated. */
  size0 = 0;
  size1 = 0;
  /** scaleY multiplier — squashes round shapes into lozenges. */
  aspect = 1;
  /** Extra length per unit of speed, applied only when aligned to velocity. */
  stretch = 0;
  alignToVelocity = false;
  alpha0 = 1;
  /** Fraction of life spent fading in / out. */
  fadeIn = 0.06;
  fadeOut = 0.45;
  /** 0 = steady, 1 = full twinkle. */
  flicker = 0;
  flickerRate = 26;
  flickerPhase = 0;
  /** Tint pre-swapped to BGR, ready to be OR'd with the alpha byte. */
  tintBgr = 0xffffff;
  /**
   * Tint at the END of life, also BGR. Defaults to `tintBgr`, i.e. no ramp.
   *
   * A hot fragment radiates as a blackbody and visibly cools along its arc —
   * white-hot, straw, orange, dull red — over a few hundred milliseconds, which
   * is the most-noticed physical property of a real spark. Without this every
   * particle held one constant hue for its whole life and merely faded out, so
   * a burst read as tinted confetti dissolving rather than as pyro.
   */
  tintBgr1 = 0xffffff;
  /**
   * Radians per second of out-of-plane tumble. `0` disables it.
   *
   * Real confetti is a flat sheet turning about a random axis: its projected
   * height passes through zero twice per revolution as it goes edge-on, then
   * flashes back to full. That alternating flash is the entire visual signature
   * of paper falling, and `spin` alone (which rotates in-plane) cannot produce
   * it — a fixed-aspect sprite spinning in-plane reads as a rotating pill.
   */
  tumbleRate = 0;
  tumblePhase = 0;

  constructor(index: number, texture: Texture) {
    this.index = index;
    this.texture = texture;
  }
}

/* ----------------------------------------------------------------- system -- */

export interface ParticleSystemOptions {
  /** Hard ceiling on live particles. Never exceeded, never grown. Default 1800. */
  capacity?: number;
  /** Global density multiplier, 1 = as designed. Default 1. */
  intensity?: number;
  /** Far fewer particles and no lingering trails. Default false. */
  reducedMotion?: boolean;
}

/** Density floor when reduced motion is on — enough to read the outcome, not to distract. */
const REDUCED_DENSITY = 0.26;

export class ParticleSystem {
  /** Add this to the stage's vfx layer. */
  readonly view: Container;

  readonly #additive: ParticleContainer<Spark>;
  readonly #normal: ParticleContainer<Spark>;
  readonly #activeAdd: Spark[] = [];
  readonly #activeNorm: Spark[] = [];

  readonly #sparks: Spark[];
  readonly #free: Int32Array;
  #freeCount: number;

  readonly #source: CanvasSource;
  readonly #frames: Record<SparkShape, Texture>;

  #intensity: number;
  #reduced: boolean;
  #destroyed = false;

  constructor(options: ParticleSystemOptions = {}) {
    const capacity = Math.max(64, Math.floor(options.capacity ?? 1800));
    this.#intensity = options.intensity ?? 1;
    this.#reduced = options.reducedMotion ?? false;

    const atlas = bakeAtlas();
    this.#source = atlas.source;
    this.#frames = atlas.frames;

    this.#sparks = new Array<Spark>(capacity);
    this.#free = new Int32Array(capacity);
    for (let i = 0; i < capacity; i++) {
      this.#sparks[i] = new Spark(i, this.#frames.dot);
      this.#free[i] = capacity - 1 - i;
    }
    this.#freeCount = capacity;

    // Every property is dynamic: the pool compacts every frame, so the particle
    // at a given buffer slot changes identity constantly and nothing can be
    // treated as static. The upload is one tight generated loop over the live
    // sparks only, so the cost tracks what is on screen, not the pool size.
    const dynamicProperties = {
      vertex: true,
      position: true,
      rotation: true,
      uvs: true,
      color: true,
    };

    this.#normal = new ParticleContainer<Spark>({
      dynamicProperties,
      texture: this.#frames.dot,
      particles: this.#activeNorm,
      blendMode: 'normal',
      label: 'vfx-particles-normal',
    });
    this.#additive = new ParticleContainer<Spark>({
      dynamicProperties,
      texture: this.#frames.dot,
      particles: this.#activeAdd,
      blendMode: 'add',
      label: 'vfx-particles-add',
    });

    this.view = new Container({ label: 'vfx-particles' });
    // Matter behind light: dust and confetti first, glow washing over them.
    this.view.addChild(this.#normal, this.#additive);
  }

  /* --------------------------------------------------------------- config -- */

  get intensity(): number {
    return this.#intensity;
  }

  setIntensity(value: number): void {
    this.#intensity = Math.max(0, value);
  }

  get reducedMotion(): boolean {
    return this.#reduced;
  }

  setReducedMotion(value: boolean): void {
    this.#reduced = value;
  }

  /** Live particle count, for the debug overlay. */
  get activeCount(): number {
    return this.#activeAdd.length + this.#activeNorm.length;
  }

  get capacity(): number {
    return this.#sparks.length;
  }

  /* ------------------------------------------------------------------ sim -- */

  /**
   * Advance the simulation. `dt` is in seconds; call this once per frame,
   * before rendering.
   */
  update(dt: number): void {
    if (this.#destroyed) return;
    const step = clamp(dt, 0, MAX_STEP);
    if (step <= 0) return;
    this.#step(this.#activeAdd, step);
    this.#step(this.#activeNorm, step);
  }

  #step(list: Spark[], dt: number): void {
    for (let i = list.length - 1; i >= 0; i--) {
      const p = list[i];

      if (p.delay > 0) {
        p.delay -= dt;
        p.color = p.tintBgr; // alpha byte 0 — staged but not yet visible
        continue;
      }

      p.life += dt;
      if (p.life >= p.maxLife) {
        const last = list.pop() as Spark;
        if (i < list.length) list[i] = last;
        this.#free[this.#freeCount++] = p.index;
        continue;
      }

      const t = p.life / p.maxLife;

      if (p.gravity !== 0) p.vy += p.gravity * dt;
      if (p.swirl !== 0) {
        const a = p.swirl * dt;
        const c = Math.cos(a);
        const s = Math.sin(a);
        const vx = p.vx * c - p.vy * s;
        p.vy = p.vx * s + p.vy * c;
        p.vx = vx;
      }
      if (p.drag !== 0) {
        const d = 1 / (1 + p.drag * dt);
        p.vx *= d;
        p.vy *= d;
      }
      p.x += p.vx * dt;
      p.y += p.vy * dt;

      const base = (p.size0 + (p.size1 - p.size0) * t) * INV_FRAME;
      if (p.alignToVelocity) {
        p.rotation = Math.atan2(p.vy, p.vx);
        const speed = Math.sqrt(p.vx * p.vx + p.vy * p.vy);
        p.scaleX = base * (1 + p.stretch * speed);
        p.scaleY = base * p.aspect;
      } else {
        p.rotation += p.spin * dt;
        p.scaleX = base;
        // The 0.06 floor keeps a one-pixel edge visible at the edge-on frame
        // rather than the sprite vanishing outright.
        p.scaleY =
          base *
          p.aspect *
          (p.tumbleRate === 0
            ? 1
            : 0.06 + 0.94 * Math.abs(Math.cos(p.life * p.tumbleRate + p.tumblePhase)));
      }

      let a = p.alpha0;
      if (p.fadeIn > 0 && t < p.fadeIn) a *= t / p.fadeIn;
      const hold = 1 - p.fadeOut;
      if (p.fadeOut > 0 && t > hold) a *= 1 - (t - hold) / p.fadeOut;
      if (p.flicker !== 0) {
        a *= 1 - p.flicker * 0.5 * (1 + Math.sin(p.life * p.flickerRate + p.flickerPhase));
      }

      // Same packing as Pixi's own Particle: BGR tint plus alpha in the top byte.
      const c0 = p.tintBgr;
      const c1 = p.tintBgr1;
      const tint =
        c0 === c1
          ? c0
          : (((c0 & 0xff) + (((c1 & 0xff) - (c0 & 0xff)) * t)) | 0) |
            ((((c0 >>> 8) & 0xff) + ((((c1 >>> 8) & 0xff) - ((c0 >>> 8) & 0xff)) * t)) << 8) |
            ((((c0 >>> 16) & 0xff) + ((((c1 >>> 16) & 0xff) - ((c0 >>> 16) & 0xff)) * t)) << 16);
      p.color = tint + (((a * 255) | 0) << 24);
    }
  }

  /** Kill everything immediately and return the whole pool. */
  /**
   * Push one small burst of each kind through the pipe at load.
   *
   * The two `ParticleContainer`s draw nothing while the pool is empty, so their
   * buffers are not allocated and their shader is not compiled until the first
   * burst — which by definition is the first thing in the round worth
   * celebrating. Warming with real bursts rather than a synthetic one means the
   * buffers are sized for the traffic they will actually see.
   */
  warm(): void {
    const x = DESIGN.width / 2;
    const y = DESIGN.height / 2;
    this.twentyOne(x, y);
    this.fiveCard(x, y);
    this.wild(x, y);
    this.bust(x, y);
    this.streak(x, y, 1);
    this.view.alpha = WARM_ALPHA;
  }

  /** Undo {@link warm}. */
  cool(): void {
    this.view.alpha = 1;
    this.clear();
  }

  clear(): void {
    this.#recycleAll(this.#activeAdd);
    this.#recycleAll(this.#activeNorm);
  }

  #recycleAll(list: Spark[]): void {
    for (let i = 0; i < list.length; i++) this.#free[this.#freeCount++] = list[i].index;
    list.length = 0;
  }

  dispose(): void {
    if (this.#destroyed) return;
    this.#destroyed = true;
    this.clear();
    this.view.removeChildren();
    this.#additive.destroy();
    this.#normal.destroy();
    this.view.destroy();
    for (const shape of SHAPES) this.#frames[shape].destroy(false);
    this.#source.destroy();
  }

  /* -------------------------------------------------------------- spawning -- */

  /** Scaled emitter count. Always emits at least one so an outcome is never silent. */
  #n(count: number): number {
    return Math.max(1, Math.round(count * this.#density));
  }

  /** Scaled count for decorative emitters, which may legitimately drop to zero. */
  #nOpt(count: number): number {
    return Math.round(count * this.#density);
  }

  get #density(): number {
    return this.#intensity * (this.#reduced ? REDUCED_DENSITY : 1);
  }

  /**
   * Take a spark from the pool and reset it. When the pool is exhausted the
   * oldest live spark is stolen, so this never allocates and never fails.
   */
  #spawn(shape: SparkShape, additive: boolean): Spark {
    const p = this.#take();

    p.texture = this.#frames[shape];
    p.additive = additive;
    p.x = 0;
    p.y = 0;
    p.vx = 0;
    p.vy = 0;
    p.gravity = 0;
    p.drag = 0;
    p.swirl = 0;
    p.spin = 0;
    p.rotation = 0;
    p.delay = 0;
    p.life = 0;
    p.maxLife = 1;
    p.size0 = cw(0.08);
    p.size1 = cw(0.02);
    p.aspect = 1;
    p.stretch = 0;
    p.alignToVelocity = false;
    p.alpha0 = 1;
    p.fadeIn = 0.06;
    p.fadeOut = 0.45;
    p.flicker = 0;
    p.flickerRate = 26;
    p.flickerPhase = 0;
    p.tintBgr = 0xffffff;
    // Reset alongside `tintBgr`: the pool is reused, so a leftover end-tint or
    // tumble from a previous burst would follow a recycled spark into the next.
    p.tintBgr1 = 0xffffff;
    p.tumbleRate = 0;
    p.tumblePhase = 0;
    p.scaleX = 0;
    p.scaleY = 0;
    p.color = 0;

    (additive ? this.#activeAdd : this.#activeNorm).push(p);
    return p;
  }

  #take(): Spark {
    if (this.#freeCount > 0) return this.#sparks[this.#free[--this.#freeCount]];
    // Pool exhausted: steal from the longer list, whose head is the stalest
    // slot after compaction. O(1), and preferable to dropping the new burst.
    const list = this.#activeAdd.length >= this.#activeNorm.length ? this.#activeAdd : this.#activeNorm;
    const victim = list[0];
    const last = list.pop() as Spark;
    if (list.length > 0) list[0] = last;
    return victim;
  }

  /** Expanding shockwave ring. */
  #ring(
    x: number,
    y: number,
    rgb: number,
    from: number,
    to: number,
    life: number,
    alpha: number,
    delay = 0,
  ): void {
    const p = this.#spawn('ring', true);
    p.x = x;
    p.y = y;
    p.tintBgr = toBgr(rgb);
    p.size0 = from;
    p.size1 = to;
    p.maxLife = life;
    p.alpha0 = alpha;
    p.fadeIn = 0.05;
    p.fadeOut = 0.78;
    p.delay = delay;
  }

  /** The soft bloom at the heart of a burst. Suppressed under reduced motion. */
  #core(
    x: number,
    y: number,
    rgb: number,
    from: number,
    to: number,
    life: number,
    alpha: number,
    delay = 0,
  ): void {
    if (this.#reduced) return;
    const p = this.#spawn('glow', true);
    p.x = x;
    p.y = y;
    p.tintBgr = toBgr(rgb);
    p.size0 = from;
    p.size1 = to;
    p.maxLife = life;
    p.alpha0 = alpha;
    p.fadeIn = 0.08;
    p.fadeOut = 0.85;
    p.delay = delay;
  }

  /* --------------------------------------------------------------- effects -- */

  /**
   * **21 — mint.** Crisp and precise: one tight shockwave and an evenly spaced
   * ring of shards that snap outward under heavy drag and stop dead, rather
   * than drifting. Reads as an exact, deliberate hit.
   */
  twentyOne(x: number, y: number): void {
    this.#core(x, y, palette.mint, cw(0.42), cw(0.95), 0.22, 0.5);
    this.#ring(x, y, palette.mint, cw(0.16), cw(1.5), 0.42, 0.8);

    const shards = this.#n(20);
    const offset = Math.random() * TAU;
    for (let i = 0; i < shards; i++) {
      // An even angular grid — the precision is the whole point of this one.
      const angle = offset + (i / shards) * TAU + rand(-0.05, 0.05);
      const speed = rand(4.2, 5.0) * SPEED;
      const p = this.#spawn('shard', true);
      p.x = x;
      p.y = y;
      p.vx = Math.cos(angle) * speed;
      p.vy = Math.sin(angle) * speed;
      p.drag = 8.5;
      p.maxLife = rand(0.3, 0.4);
      p.size0 = cw(0.24);
      p.size1 = cw(0.09);
      p.aspect = 0.85;
      p.alignToVelocity = true;
      p.stretch = 0.00045;
      p.tintBgr = toBgr(mixRgb(palette.mint, palette.white, rand(0, 0.45)));
      p.fadeIn = 0.02;
      p.fadeOut = 0.6;
    }

    const dots = this.#n(14);
    for (let i = 0; i < dots; i++) {
      const angle = rand(0, TAU);
      const speed = rand(1.4, 3.1) * SPEED;
      const p = this.#spawn('dot', true);
      p.x = x;
      p.y = y;
      p.vx = Math.cos(angle) * speed;
      p.vy = Math.sin(angle) * speed;
      p.drag = 3.4;
      p.gravity = cw(1.2);
      p.maxLife = rand(0.55, 0.9);
      p.size0 = cw(0.07);
      p.size1 = cw(0.008);
      p.tintBgr = toBgr(mixRgb(palette.mint, palette.white, rand(0.1, 0.7)));
      p.tintBgr1 = toBgr(mixRgb(palette.mint, palette.plum, 0.45));
      p.fadeOut = 0.55;
    }

    const glints = this.#nOpt(4);
    for (let i = 0; i < glints; i++) {
      const angle = Math.PI / 4 + (i / 4) * TAU;
      const speed = rand(1.6, 2.2) * SPEED;
      const p = this.#spawn('spark', true);
      p.x = x;
      p.y = y;
      p.vx = Math.cos(angle) * speed;
      p.vy = Math.sin(angle) * speed;
      p.drag = 9;
      p.spin = randSign() * rand(2, 4);
      p.maxLife = rand(0.4, 0.55);
      p.size0 = cw(0.22);
      p.size1 = cw(0.04);
      p.tintBgr = toBgr(palette.mint);
      p.fadeIn = 0.03;
      p.fadeOut = 0.7;
    }
  }

  /**
   * **5-card — gold.** The biggest per-pile payout, so this one is generous
   * rather than sharp: two staggered rings, a wide spray of sparks, and real
   * confetti chips that tumble and fall under gravity on the non-additive pass
   * so they read as solid money rather than light.
   */
  fiveCard(x: number, y: number): void {
    this.#core(x, y, palette.gold, cw(0.55), cw(1.1), 0.3, 0.55);
    this.#ring(x, y, palette.gold, cw(0.13), cw(1.7), 0.5, 0.85);
    this.#ring(x, y, palette.goldDeep, cw(0.13), cw(1.32), 0.46, 0.55, 0.09);

    const dots = this.#n(36);
    for (let i = 0; i < dots; i++) {
      const angle = rand(0, TAU);
      const speed = rand(0.75, 3.8) * SPEED;
      const p = this.#spawn('dot', true);
      p.x = x;
      p.y = y;
      p.vx = Math.cos(angle) * speed;
      p.vy = Math.sin(angle) * speed;
      p.drag = 1.8;
      p.gravity = cw(2.6);
      p.maxLife = rand(0.7, 1.3);
      p.size0 = cw(0.08);
      p.size1 = cw(0.008);
      p.tintBgr = toBgr(mixRgb(palette.gold, palette.white, rand(0, 0.6)));
      p.tintBgr1 = toBgr(mixRgb(palette.goldDeep, palette.plum, 0.35));
      // Measured at 0.79 diameters of travel per frame — past the ~0.5
      // threshold where the eye resolves individual frames and the trail
      // breaks into a dotted line. Stretching along travel is what turns
      // that back into a continuous streak.
      p.alignToVelocity = true;
      p.stretch = 0.0009;
      p.aspect = 0.65;
      p.fadeOut = 0.5;
    }

    const chips = this.#nOpt(16);
    for (let i = 0; i < chips; i++) {
      // Biased upward so the payout is thrown, then falls back onto the felt.
      const angle = rand(-Math.PI * 0.92, -Math.PI * 0.08);
      const speed = rand(1.3, 3.2) * SPEED;
      const p = this.#spawn('chip', false);
      p.x = x;
      p.y = y;
      p.vx = Math.cos(angle) * speed;
      p.vy = Math.sin(angle) * speed;
      p.drag = 0.6;
      p.gravity = cw(4.5);
      p.spin = randSign() * rand(4, 9);
      p.rotation = rand(0, TAU);
      p.maxLife = rand(1.1, 1.7);
      p.size0 = cw(0.13);
      p.size1 = cw(0.11);
      p.aspect = rand(0.55, 1);
      p.tintBgr = toBgr(mixRgb(palette.gold, palette.goldDeep, rand(0, 0.8)));
      p.tumbleRate = (Math.random() < 0.5 ? -1 : 1) * rand(5, 11);
      p.tumblePhase = rand(0, TAU);
      p.alpha0 = 0.95;
      p.fadeIn = 0.02;
      p.fadeOut = 0.25;
    }

    const twinkles = this.#nOpt(10);
    for (let i = 0; i < twinkles; i++) {
      const angle = rand(0, TAU);
      const speed = rand(0.6, 1.9) * SPEED;
      const p = this.#spawn('spark', true);
      p.x = x;
      p.y = y;
      p.vx = Math.cos(angle) * speed;
      p.vy = Math.sin(angle) * speed;
      p.drag = 4;
      p.gravity = cw(0.6);
      p.spin = randSign() * rand(0.6, 2);
      p.maxLife = rand(0.6, 1.05);
      p.size0 = cw(0.24);
      p.size1 = cw(0.05);
      p.tintBgr = toBgr(mixRgb(palette.gold, palette.white, rand(0, 0.35)));
      p.flicker = 0.55;
      p.flickerRate = rand(20, 34);
      p.flickerPhase = rand(0, TAU);
      p.fadeOut = 0.6;
    }
  }

  /**
   * **Wild — violet.** The rescue card, so this has to feel like relief: three
   * staggered rings opening outward, a coherent swirl (one direction per burst,
   * never a random tangle), a double shard corona, and a tail of slow motes
   * that float *up* and twinkle out well after the rest has gone.
   */
  wild(x: number, y: number): void {
    const swirl = randSign() * rand(2.1, 3.0);

    this.#core(x, y, palette.violetLight, cw(0.6), cw(1.5), 0.36, 0.7);
    this.#ring(x, y, palette.violetLight, cw(0.13), cw(1.5), 0.5, 0.9);
    this.#ring(x, y, palette.violet, cw(0.13), cw(1.9), 0.56, 0.6, 0.07);
    this.#ring(x, y, palette.violetLight, cw(0.13), cw(2.3), 0.62, 0.34, 0.15);

    const dots = this.#n(46);
    for (let i = 0; i < dots; i++) {
      const angle = rand(0, TAU);
      const speed = rand(1.0, 4.1) * SPEED;
      const p = this.#spawn('dot', true);
      p.x = x;
      p.y = y;
      p.vx = Math.cos(angle) * speed;
      p.vy = Math.sin(angle) * speed;
      p.swirl = swirl;
      p.drag = 1.4;
      p.gravity = cw(-0.3); // rising — the tension coming off the pile
      p.maxLife = rand(0.85, 1.6);
      p.size0 = cw(0.09);
      p.size1 = cw(0.01);
      p.tintBgr = toBgr(
        mixRgb(mixRgb(palette.violet, palette.violetLight, rand(0, 1)), palette.white, rand(0, 0.4)),
      );
      p.fadeOut = 0.55;
    }

    const shards = this.#n(18);
    for (let i = 0; i < shards; i++) {
      const inner = i % 3 === 0;
      const angle = (i / shards) * TAU + (inner ? 0.3 : 0) + rand(-0.08, 0.08);
      const speed = (inner ? rand(2.4, 3.0) : rand(3.6, 4.4)) * SPEED;
      const p = this.#spawn('shard', true);
      p.x = x;
      p.y = y;
      p.vx = Math.cos(angle) * speed;
      p.vy = Math.sin(angle) * speed;
      p.drag = 6;
      p.maxLife = rand(0.4, 0.55);
      p.size0 = cw(0.28);
      p.size1 = cw(0.1);
      p.aspect = 0.9;
      p.alignToVelocity = true;
      p.stretch = 0.0005;
      p.tintBgr = toBgr(mixRgb(palette.violetLight, palette.white, rand(0, 0.5)));
      p.fadeIn = 0.02;
      p.fadeOut = 0.62;
    }

    const motes = this.#nOpt(this.#reduced ? 0 : 14);
    for (let i = 0; i < motes; i++) {
      const angle = rand(-Math.PI * 0.85, -Math.PI * 0.15);
      const speed = rand(0.3, 0.95) * SPEED;
      const p = this.#spawn('mote', true);
      p.x = x + rand(-cw(0.5), cw(0.5));
      p.y = y + rand(-cw(0.3), cw(0.3));
      p.vx = Math.cos(angle) * speed;
      p.vy = Math.sin(angle) * speed;
      p.drag = 0.9;
      p.gravity = cw(-0.55);
      p.maxLife = rand(1.4, 2.1);
      p.size0 = cw(0.05);
      p.size1 = cw(0.075);
      p.tintBgr = toBgr(mixRgb(palette.violetLight, palette.white, rand(0.2, 0.9)));
      p.tintBgr1 = toBgr(mixRgb(palette.violet, palette.plum, 0.3));
      p.alignToVelocity = true;
      p.stretch = 0.0009;
      p.aspect = 0.65;
      p.alpha0 = 0.9;
      p.flicker = 0.7;
      p.flickerRate = rand(9, 18);
      p.flickerPhase = rand(0, TAU);
      p.fadeIn = 0.14;
      p.fadeOut = 0.5;
    }

    const glints = this.#nOpt(8);
    for (let i = 0; i < glints; i++) {
      const angle = rand(0, TAU);
      const speed = rand(0.7, 2.1) * SPEED;
      const p = this.#spawn('spark', true);
      p.x = x;
      p.y = y;
      p.vx = Math.cos(angle) * speed;
      p.vy = Math.sin(angle) * speed;
      p.swirl = swirl * 0.6;
      p.drag = 3.4;
      p.spin = randSign() * rand(1, 2.4);
      p.maxLife = rand(0.55, 0.85);
      p.size0 = cw(0.35);
      p.size1 = cw(0.07);
      p.tintBgr = toBgr(mixRgb(palette.violetLight, palette.white, rand(0.1, 0.6)));
      p.fadeOut = 0.65;
    }
  }

  /**
   * **Bust — rose.** Deliberately not a firework. No shockwave, no bloom core,
   * no additive flash: just a short cough of dust on the *normal* blend pass
   * that expands, sags and dies in half a second, plus a few dark fragments
   * falling off the pile. Unhappy, over quickly, never celebratory.
   */
  bust(x: number, y: number): void {
    const puffs = this.#n(18);
    for (let i = 0; i < puffs; i++) {
      // Outward and downward — the pile collapsing, not exploding.
      const angle = rand(-Math.PI * 0.35, Math.PI * 1.35);
      const speed = rand(0.2, 1.0) * SPEED;
      const p = this.#spawn('dust', false);
      p.x = x + rand(-cw(0.28), cw(0.28));
      p.y = y + rand(-cw(0.16), cw(0.16));
      p.vx = Math.cos(angle) * speed;
      p.vy = Math.sin(angle) * speed * 0.5 + rand(0.1, 0.5) * SPEED;
      p.drag = 3.2;
      p.gravity = cw(1.9);
      p.spin = randSign() * rand(0.4, 1.3);
      p.rotation = rand(0, TAU);
      p.maxLife = rand(0.35, 0.6);
      p.size0 = cw(0.15);
      p.size1 = cw(0.34);
      p.alpha0 = rand(0.28, 0.46);
      p.tintBgr = toBgr(mixRgb(palette.rose, palette.feltDeep, rand(0.15, 0.7)));
      p.fadeIn = 0.08;
      p.fadeOut = 0.72;
    }

    const debris = this.#nOpt(7);
    for (let i = 0; i < debris; i++) {
      const angle = rand(Math.PI * 0.12, Math.PI * 0.88);
      const speed = rand(0.6, 1.6) * SPEED;
      const p = this.#spawn('shard', false);
      p.x = x + rand(-cw(0.3), cw(0.3));
      p.y = y;
      p.vx = Math.cos(angle) * speed;
      p.vy = Math.sin(angle) * speed;
      p.drag = 0.8;
      p.gravity = cw(4.5);
      p.maxLife = rand(0.4, 0.62);
      p.size0 = cw(0.17);
      p.size1 = cw(0.11);
      p.aspect = 1.2;
      p.alignToVelocity = true;
      p.tintBgr = toBgr(mixRgb(palette.rose, palette.plum, rand(0.2, 0.6)));
      p.tintBgr1 = toBgr(palette.feltDeep);
      p.stretch = 0.0006;
      p.alpha0 = 0.8;
      p.fadeIn = 0.04;
      p.fadeOut = 0.45;
    }

    // One brief rose flick so the eye is drawn to which pile died — tiny, and
    // gone before it can be mistaken for a reward.
    const flicks = this.#nOpt(5);
    for (let i = 0; i < flicks; i++) {
      const angle = rand(0, TAU);
      const speed = rand(0.5, 1.4) * SPEED;
      const p = this.#spawn('dot', true);
      p.x = x;
      p.y = y;
      p.vx = Math.cos(angle) * speed;
      p.vy = Math.sin(angle) * speed;
      p.drag = 7;
      p.maxLife = rand(0.14, 0.22);
      p.size0 = cw(0.06);
      p.size1 = cw(0.005);
      p.alpha0 = 0.55;
      p.tintBgr = toBgr(palette.rose);
      p.fadeOut = 0.7;
    }
  }

  /**
   * **Streak — escalating.** `level` is 1..5, rendered as the 2x..6x callouts.
   * The ladder is legible by feel: more rings, more sparks, a longer throw, and
   * the hue creeping from money-gold toward the wild's violet as it climbs. At
   * level 5 it stops being a burst and becomes an event — a fountain out of the
   * pile and a band of light across the width of the table.
   */
  streak(x: number, y: number, level: number): void {
    const l = clamp(Math.round(level), 1, LADDER_MAX);
    // Front-loaded like every other part of the streak response, and taking its
    // colour from the shared ladder so the burst, the badge, the rim and the
    // letterbox spill are all the same hue at the same instant.
    const k = momentum(l);
    const hue = ladderColour(l);

    this.#core(x, y, hue, cw(0.4), cw(0.8 + 0.5 * l), 0.24 + 0.04 * l, 0.35 + 0.07 * l);
    this.#ring(x, y, hue, cw(0.13), cw(0.9 + 0.28 * l), 0.4 + 0.04 * l, 0.7);
    if (l >= 3) this.#ring(x, y, palette.gold, cw(0.13), cw(0.7 + 0.3 * l), 0.44, 0.42, 0.08);
    if (l >= 5) this.#ring(x, y, palette.violetLight, cw(0.13), cw(2.6), 0.7, 0.5, 0.16);

    const dots = this.#n(12 + 10 * l);
    for (let i = 0; i < dots; i++) {
      const angle = rand(0, TAU);
      const speed = rand(0.9, 2.2 + 0.5 * l) * SPEED;
      const p = this.#spawn('dot', true);
      p.x = x;
      p.y = y;
      p.vx = Math.cos(angle) * speed;
      p.vy = Math.sin(angle) * speed;
      p.drag = 2.2;
      p.gravity = cw(1.8);
      p.maxLife = rand(0.5, 0.8 + 0.12 * l);
      p.size0 = cw(0.06 + 0.008 * l);
      p.size1 = cw(0.008);
      p.tintBgr = toBgr(mixRgb(hue, palette.white, rand(0, 0.55)));
      p.tintBgr1 = toBgr(mixRgb(hue, palette.plum, 0.5));
      p.alignToVelocity = true;
      p.stretch = 0.0009;
      p.aspect = 0.65;
      p.fadeOut = 0.5;
    }

    if (l >= 3) {
      const chips = this.#nOpt(3 * l);
      for (let i = 0; i < chips; i++) {
        const angle = rand(-Math.PI * 0.9, -Math.PI * 0.1);
        const speed = rand(1.4, 3.0) * SPEED;
        const p = this.#spawn('chip', false);
        p.x = x;
        p.y = y;
        p.vx = Math.cos(angle) * speed;
        p.vy = Math.sin(angle) * speed;
        p.drag = 0.6;
        p.gravity = cw(4.4);
        p.spin = randSign() * rand(4, 10);
        p.rotation = rand(0, TAU);
        p.maxLife = rand(1.0, 1.6);
        p.size0 = cw(0.12);
        p.size1 = cw(0.1);
        p.aspect = rand(0.5, 1);
        p.tintBgr = toBgr(mixRgb(palette.gold, palette.goldDeep, rand(0, 0.7)));
        p.alpha0 = 0.95;
        p.fadeIn = 0.02;
        p.fadeOut = 0.25;
      }
    }

    if (l >= 4) {
      // The fountain: a narrow column thrown straight up out of the pile.
      const jets = this.#n(14 * l);
      for (let i = 0; i < jets; i++) {
        const angle = -Math.PI / 2 + rand(-0.32, 0.32);
        const speed = rand(3.4, 6.2) * SPEED;
        const p = this.#spawn('mote', true);
        p.x = x + rand(-cw(0.2), cw(0.2));
        p.y = y;
        p.vx = Math.cos(angle) * speed;
        p.vy = Math.sin(angle) * speed;
        p.drag = 0.5;
        p.gravity = cw(5.2);
        p.maxLife = rand(0.9, 1.5);
        p.size0 = cw(0.07);
        p.size1 = cw(0.015);
        p.tintBgr = toBgr(mixRgb(hue, palette.white, rand(0.1, 0.8)));
        p.tintBgr1 = toBgr(mixRgb(hue, palette.plum, 0.55));
        // The fastest particles in the game: measured at 1.48 of their own
        // diameter per frame, so each mote jumped a clear gap of its own size
        // every frame and the fountain read as a dotted line. At this stretch
        // the mote elongates to ~2x and the frames overlap, which is what a
        // motion streak actually is.
        p.alignToVelocity = true;
        p.stretch = 0.0009;
        p.aspect = 0.6;
        p.flicker = 0.4;
        p.flickerRate = rand(18, 30);
        p.flickerPhase = rand(0, TAU);
        p.fadeOut = 0.4;
      }
    }

    if (l >= 5) {
      // Level 5 is the ceiling of the ladder, so it leaves the pile entirely and
      // takes the whole width of the table with it.
      const band = this.#n(40);
      for (let i = 0; i < band; i++) {
        const p = this.#spawn('mote', true);
        p.x = rand(0, DESIGN.width);
        p.y = y + rand(-cw(0.5), cw(0.9));
        p.vx = rand(-0.35, 0.35) * SPEED;
        p.vy = rand(-1.5, -0.5) * SPEED;
        p.drag = 0.7;
        p.gravity = cw(-0.4);
        p.delay = rand(0, 0.3);
        p.maxLife = rand(1.2, 2.0);
        p.size0 = cw(0.05);
        p.size1 = cw(0.09);
        p.alpha0 = 0.85;
        p.tintBgr = toBgr(
          mixRgb(palette.gold, palette.violetLight, rand(0, 1)),
        );
        p.flicker = 0.65;
        p.flickerRate = rand(8, 16);
        p.flickerPhase = rand(0, TAU);
        p.fadeIn = 0.12;
        p.fadeOut = 0.5;
      }

      const glints = this.#nOpt(12);
      for (let i = 0; i < glints; i++) {
        const angle = rand(0, TAU);
        const speed = rand(0.8, 2.4) * SPEED;
        const p = this.#spawn('spark', true);
        p.x = x;
        p.y = y;
        p.vx = Math.cos(angle) * speed;
        p.vy = Math.sin(angle) * speed;
        p.drag = 3.2;
        p.spin = randSign() * rand(1, 3);
        p.maxLife = rand(0.6, 0.95);
        p.size0 = cw(0.36);
        p.size1 = cw(0.06);
        p.tintBgr = toBgr(mixRgb(palette.gold, palette.white, rand(0, 0.5)));
        p.fadeOut = 0.6;
      }
    }
  }

  /**
   * **Perfect game — the whole table.** Rare enough to be worth the budget:
   * seven staggered bursts walked across the felt, a rain of confetti falling
   * in from above the frame, and two slow bloom cores at the centre. All of it
   * is staggered with per-particle `delay`, so it plays out over ~2 seconds
   * from a single call with no timers, no closures and nothing to clean up.
   */
  /**
   * The level-up impulse: soft motes pushed outward, in the level key.
   *
   * Deliberately `dot` and `glow` only — no shards, no sparks, no straight
   * radiating lines. Hard thin geometry launched from a point reads as
   * hairlines or as an even fan of spokes, which is exactly what made the first
   * pass of this effect look cheap. Round, soft, drifting: a puff of cool
   * light, not a firework.
   *
   * @param speed - design units per second; a milestone pushes harder.
   */
  levelUp(x: number, y: number, speed: number): void {
    if (this.#reduced) return;

    const motes = this.#n(18);
    for (let i = 0; i < motes; i++) {
      const angle = rand(0, TAU);
      // Slight spread rather than a fixed radius, so the leading edge is a
      // cloud rather than a ring travelling outward in lockstep.
      const v = speed * rand(0.55, 1);
      const p = this.#spawn('dot', true);
      p.x = x;
      p.y = y;
      p.vx = Math.cos(angle) * v;
      p.vy = Math.sin(angle) * v;
      p.drag = 3.1;
      // Barely any: motes are light, and light does not fall.
      p.gravity = cw(0.25);
      p.maxLife = rand(0.5, 0.95);
      p.size0 = cw(0.055);
      p.size1 = cw(0.006);
      p.tintBgr = toBgr(mixRgb(palette.azureLight, palette.white, rand(0, 0.45)));
      p.tintBgr1 = toBgr(palette.azure);
      p.fadeIn = 0.03;
      p.fadeOut = 0.62;
    }

    const halo = this.#nOpt(3);
    for (let i = 0; i < halo; i++) {
      const p = this.#spawn('glow', true);
      p.x = x;
      p.y = y;
      p.vx = rand(-0.2, 0.2) * speed;
      p.vy = rand(-0.2, 0.2) * speed;
      p.drag = 5;
      p.maxLife = rand(0.45, 0.7);
      p.size0 = cw(0.3);
      p.size1 = cw(0.9);
      p.tintBgr = toBgr(palette.azure);
      p.fadeIn = 0.05;
      p.fadeOut = 0.9;
    }
  }

  perfectGame(): void {
    const centreY = DESIGN.height * 0.42;
    const hues = [palette.gold, palette.mint, palette.violetLight] as const;
    const sites = 7;

    for (let s = 0; s < sites; s++) {
      const x = DESIGN.width * (0.11 + (0.78 * s) / (sites - 1));
      const y = centreY + (s % 2 === 0 ? -1 : 1) * DESIGN.height * 0.11;
      const hue = hues[s % hues.length];
      const delay = s * 0.16;

      this.#core(x, y, hue, cw(0.5), cw(1.4), 0.34, 0.5, delay);
      this.#ring(x, y, hue, cw(0.13), cw(1.9), 0.55, 0.75, delay);

      const dots = this.#n(24);
      for (let i = 0; i < dots; i++) {
        const angle = rand(0, TAU);
        const speed = rand(1.0, 4.0) * SPEED;
        const p = this.#spawn('dot', true);
        p.x = x;
        p.y = y;
        p.vx = Math.cos(angle) * speed;
        p.vy = Math.sin(angle) * speed;
        p.drag = 1.7;
        p.gravity = cw(2.2);
        p.delay = delay;
        p.maxLife = rand(0.7, 1.4);
        p.size0 = cw(0.08);
        p.size1 = cw(0.008);
        p.tintBgr = toBgr(mixRgb(hue, palette.white, rand(0, 0.6)));
        p.fadeOut = 0.5;
      }

      const glints = this.#nOpt(6);
      for (let i = 0; i < glints; i++) {
        const angle = rand(0, TAU);
        const speed = rand(0.6, 1.8) * SPEED;
        const p = this.#spawn('spark', true);
        p.x = x;
        p.y = y;
        p.vx = Math.cos(angle) * speed;
        p.vy = Math.sin(angle) * speed;
        p.drag = 3.6;
        p.spin = randSign() * rand(1, 3);
        p.delay = delay;
        p.maxLife = rand(0.6, 1.0);
        p.size0 = cw(0.3);
        p.size1 = cw(0.05);
        p.tintBgr = toBgr(mixRgb(hue, palette.white, rand(0, 0.4)));
        p.flicker = 0.5;
        p.flickerRate = rand(18, 30);
        p.flickerPhase = rand(0, TAU);
        p.fadeOut = 0.6;
      }
    }

    const confetti = this.#nOpt(90);
    for (let i = 0; i < confetti; i++) {
      const p = this.#spawn('chip', false);
      p.x = rand(-cw(0.2), DESIGN.width + cw(0.2));
      p.y = rand(-CARD.height * 1.4, -CARD.height * 0.2);
      p.vx = rand(-0.5, 0.5) * SPEED;
      p.vy = rand(0.4, 1.4) * SPEED;
      p.drag = 1.1;
      p.gravity = cw(1.6);
      p.spin = randSign() * rand(2, 8);
      p.rotation = rand(0, TAU);
      p.delay = rand(0, 1.2);
      p.maxLife = rand(2.4, 3.6);
      p.size0 = cw(0.13);
      p.size1 = cw(0.13);
      p.aspect = rand(0.4, 1);
      p.tintBgr = toBgr(
        mixRgb(hues[i % hues.length], palette.white, rand(0, 0.25)),
      );
      p.alpha0 = 0.95;
      p.fadeIn = 0.03;
      p.fadeOut = 0.2;
    }

    this.#core(DESIGN.width / 2, centreY, palette.gold, cw(1.0), cw(4.0), 0.9, 0.4, 0.05);
    this.#core(DESIGN.width / 2, centreY, palette.violetLight, cw(0.8), cw(3.2), 1.1, 0.28, 0.4);
  }
}
